import React, { useEffect, useMemo, useState } from 'react';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { API_BASE_URL } from '../services/api';

const NonOrderedReport = () => {
  const [summary, setSummary] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [drill, setDrill] = useState(null);
  const [details, setDetails] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [period, setPeriod] = useState('');
  const [months, setMonths] = useState([]);
  const [monthsLoading, setMonthsLoading] = useState(true);

  const request = async (params = new URLSearchParams()) => {
    const token = localStorage.getItem('authToken');
    params.set('period', period);
    const suffix = `?${params.toString()}`;
    const response = await fetch(`${API_BASE_URL}/report/non-ordered${suffix}`, {
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    });
    const json = await response.json();
    if (!response.ok || json.error) throw new Error(json.message || 'Failed to load report');
    return json.data;
  };

  useEffect(() => {
    const loadMonths = async () => {
      setMonthsLoading(true); setError('');
      try {
        const token = localStorage.getItem('authToken');
        const response = await fetch(`${API_BASE_URL}/bill/dep-bill-months`, {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        });
        const json = await response.json();
        if (!response.ok || json.error) throw new Error(json.message || 'Failed to load months');
        const data = Array.isArray(json.data) ? json.data : Array.isArray(json) ? json : json.data ? [json.data] : [];
        const availableMonths = data
          .filter((item) => item?.month && item?.period)
          .sort((a, b) => String(b.period).split(':')[0].localeCompare(String(a.period).split(':')[0]));
        setMonths(availableMonths);
        setPeriod((current) => current || availableMonths[0]?.period || '');
      } catch (err) {
        setError(err.message);
        setLoading(false);
      } finally {
        setMonthsLoading(false);
      }
    };
    loadMonths();
  }, []);

  useEffect(() => {
    if (!period) return;
    setLoading(true);
    request(new URLSearchParams()).then((data) => setSummary(Array.isArray(data) ? data : data?.summary || []))
      .catch((err) => setError(err.message)).finally(() => setLoading(false));
  }, [period]);

  const types = useMemo(() => [...new Set(summary.map((row) => row.branch_type).filter(Boolean))], [summary]);
  const districts = useMemo(() => [...new Set(summary.map((row) => row.district).filter(Boolean))], [summary]);
  const count = (district, type) => Number(summary.find((row) => row.district === district && row.branch_type === type)?.count || 0);

  const openDrill = async (district, branchType) => {
    setDrill({ district, branchType });
    setDetails([]);
    setDetailLoading(true);
    try {
      const params = new URLSearchParams({ district, branch_type: branchType });
      const data = await request(params);
      setDetails(Array.isArray(data) ? data : data?.details || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setDetailLoading(false);
    }
  };

  const exportDetails = async () => {
    const workbook = new ExcelJS.Workbook();
    const sheet = workbook.addWorksheet('Order Not Placed');
    sheet.addRow(['District', 'Type', 'Hostel Code', 'Hostel Name', 'Location', 'Contact Person', 'Mobile']);
    details.forEach((row) => sheet.addRow([
      row.district || drill?.district || '', row.branch_type || drill?.branchType || '',
      row.branch || '', row.name || '', row.location || '', row.manager || '', row.contact || '',
    ]));
    sheet.columns.forEach((column) => { column.width = 20; });
    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(new Blob([buffer]), `Order_Not_Placed_${drill?.district || 'all'}_${drill?.branchType || 'all'}.xlsx`);
  };

  return <div className="w-full max-w-7xl mx-auto p-4">
    <div className="bg-white shadow rounded-lg p-6">
      <div className="flex flex-wrap justify-between items-center gap-3 mb-4"><h1 className="text-xl font-semibold text-gray-800">Order Not Placed</h1><label className="flex items-center gap-2 text-sm font-medium text-gray-700">Month<select value={period} disabled={monthsLoading || !months.length} onChange={(event) => setPeriod(event.target.value)} className="min-w-[150px] rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"><option value="">{monthsLoading ? 'Loading months...' : 'Select month'}</option>{months.map((item) => <option key={item.period} value={item.period}>{item.month}</option>)}</select></label></div>
      {loading && <p className="text-gray-600">Loading...</p>}
      {error && <p className="text-red-600 mb-3">{error}</p>}
      <div className="overflow-x-auto">
        <table className="w-full border-collapse border border-gray-300">
          <thead><tr className="bg-gray-100"><th className="border p-3 text-left">District</th>{types.map((type) => <th key={type} className="border p-3 text-center">{type}</th>)}<th className="border p-3 text-center">All</th></tr></thead>
          <tbody>{districts.map((district) => <tr key={district}>
            <td className="border p-3 font-medium">{district}</td>
            {types.map((type) => <td key={type} className="border p-3 text-center"><button className="text-blue-700 hover:underline font-semibold" onClick={() => openDrill(district, type)}>{count(district, type)}</button></td>)}
            <td className="border p-3 text-center font-bold"><button className="text-blue-700 hover:underline" onClick={() => openDrill(district, 'all')}>{types.reduce((total, type) => total + count(district, type), 0)}</button></td>
          </tr>)}</tbody>
          <tfoot><tr className="bg-blue-50 font-bold"><td className="border p-3">All</td>{types.map((type) => <td key={type} className="border p-3 text-center"><button className="text-blue-700 hover:underline" onClick={() => openDrill('all', type)}>{districts.reduce((total, district) => total + count(district, type), 0)}</button></td>)}<td className="border p-3 text-center"><button className="text-blue-700 hover:underline" onClick={() => openDrill('all', 'all')}>{summary.reduce((total, row) => total + Number(row.count || 0), 0)}</button></td></tr></tfoot>
        </table>
      </div>
    </div>
    {drill && <div className="fixed inset-0 z-50 bg-black bg-opacity-40 flex items-center justify-center p-4"><div className="bg-white rounded-lg shadow-xl w-full max-w-6xl max-h-[85vh] flex flex-col">
      <div className="p-4 border-b flex justify-between items-center"><h2 className="font-semibold">{drill.district} - {drill.branchType}</h2><div className="flex gap-2"><button onClick={exportDetails} disabled={!details.length} className="px-3 py-2 bg-green-600 disabled:bg-gray-400 text-white rounded">Export Excel</button><button onClick={() => setDrill(null)} className="text-2xl px-2">×</button></div></div>
      <div className="p-4 overflow-auto">{detailLoading ? <p>Loading...</p> : <table className="w-full border-collapse border"><thead><tr className="bg-gray-100">{['District','Type','Hostel Code','Hostel Name','Location','Contact Person','Mobile'].map((title) => <th key={title} className="border p-2 text-left">{title}</th>)}</tr></thead><tbody>{details.map((row, index) => <tr key={row.branch || index}><td className="border p-2">{row.district}</td><td className="border p-2">{row.branch_type}</td><td className="border p-2">{row.branch}</td><td className="border p-2">{row.name}</td><td className="border p-2">{row.location}</td><td className="border p-2">{row.manager}</td><td className="border p-2">{row.contact}</td></tr>)}{!details.length && <tr><td colSpan="7" className="border p-4 text-center text-gray-500">No records</td></tr>}</tbody></table>}</div>
    </div></div>}
  </div>;
};

export default NonOrderedReport;
